/**
 * EN-836 — recording persistence helpers.
 *
 * The web recorder must always flush MediaRecorder data on a finite, periodic cadence so
 * the in-progress recording can be snapshotted to IndexedDB and recovered after a
 * mid-recording reload/crash. Starting MediaRecorder with `undefined` (no timeslice) was
 * the root cause of the Homestead recording-loss incident: audio lived only in the
 * in-memory MediaRecorder buffer until stop, so any interruption lost it entirely.
 */

/** Snapshot cadence (ms) for the production path (CHUNK_UPLOAD off). Bounds worst-case
 * mid-recording loss to this window. Partial chunks are NOT uploaded — only persisted to
 * IndexedDB; the complete file is still assembled and uploaded once at stop. */
export const PERSIST_SNAPSHOT_MS = 15_000;

/** Cadence (ms) used when the experimental CHUNK_UPLOAD flag is on. */
export const CHUNK_UPLOAD_TIMESLICE_MS = 60_000;

/**
 * The MediaRecorder timeslice to start with. Always finite so `ondataavailable` fires
 * periodically during recording (never only at stop).
 *
 * @param chunkUploadEnabled whether the CHUNK_UPLOAD feature flag is enabled
 */
export function resolveRecorderTimeslice(chunkUploadEnabled: boolean): number {
    return chunkUploadEnabled ? CHUNK_UPLOAD_TIMESLICE_MS : PERSIST_SNAPSHOT_MS;
}

/**
 * Whether incomplete-upload recovery may run right now.
 *
 * `retryIncompleteUploads` re-uploads every incomplete IndexedDB entry. During an active recording
 * the recorder writes periodic *incomplete* snapshots (bytesUploaded=0) of the in-progress file, so
 * running recovery mid-recording would upload a partial file, transcribe it, and then upload the
 * full file again at stop — a truncated transcription plus a duplicate section/doc-gen (EN-652).
 * Recovery must therefore only run when nothing is recording or uploading. Startup is naturally
 * safe (nothing records at boot); this guard is what makes the network-restore trigger safe.
 */
export function shouldRecoverIncompleteUploads(params: { isRecording: boolean; isUploading: boolean }): boolean {
    return !params.isRecording && !params.isUploading;
}

/**
 * Cross-tab recording liveness, over BroadcastChannel.
 *
 * The per-tab recovery gate can't see a recording in *another* tab, but mid-recording snapshots
 * (bytesUploaded=0) are written to shared IndexedDB every PERSIST_SNAPSHOT_MS. So a second tab
 * running recovery could upload an in-progress recording's partial file (EN-652). We ask other
 * browsing contexts whether any is actively recording; a *live* tab answers, a *crashed* one is
 * gone and can't — so a genuinely crashed recording still recovers (no crash-recovery regression).
 */
export const RECORDING_LIVENESS_CHANNEL = "happydoc-recording-liveness";
const LIVENESS_PING = "who-is-recording";
const LIVENESS_PONG = "recording-active";

/**
 * A per-tab nonce, stamped onto every ping this tab emits. Per the BroadcastChannel spec a posted
 * message is delivered to every same-named channel object EXCEPT the sending one — including this
 * tab's *own* responder instance. Without a nonce the probe's ping would be answered by its own
 * tab's responder, so recovery would falsely see "recording elsewhere" and never run. Responders
 * ignore pings carrying their own tab's nonce, so a tab never answers itself; only genuinely
 * separate tabs (different nonce) reply.
 */
const TAB_NONCE: string =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `tab-${Math.floor(Math.random() * 1e9)}-${Math.floor(Math.random() * 1e9)}`;

type LivenessPing = { kind: typeof LIVENESS_PING; nonce: string };

/** Builds the ping payload for a given tab nonce. Exported for testing the nonce guard. */
export function buildLivenessPing(nonce: string): LivenessPing {
    return { kind: LIVENESS_PING, nonce };
}

/**
 * Whether a responder holding `myNonce` should answer `data`. True only when `data` is a well-formed
 * ping from a *different* tab (nonce mismatch). A tab never answers its own ping — this is the guard
 * that stops the same-tab self-PONG. Pure and side-effect-free so it can be unit-tested without the
 * BroadcastChannel plumbing.
 */
export function shouldAnswerLivenessPing(data: unknown, myNonce: string): boolean {
    if (typeof data !== "object" || data === null) return false;
    const ping = data as Partial<LivenessPing>;
    return ping.kind === LIVENESS_PING && typeof ping.nonce === "string" && ping.nonce !== myNonce;
}

/** Whether `data` is a liveness PONG (i.e. some other tab reported an active recording). */
export function isLivenessPong(data: unknown): boolean {
    return data === LIVENESS_PONG;
}

/**
 * Resolves true if any *other* tab reports an active recording within `timeoutMs`.
 * Resolves false when the BroadcastChannel API is unavailable (feature-detected) or nobody answers.
 */
export function isRecordingActiveElsewhere(timeoutMs = 250): Promise<boolean> {
    if (typeof BroadcastChannel === "undefined") return Promise.resolve(false);
    return new Promise((resolve) => {
        const channel = new BroadcastChannel(RECORDING_LIVENESS_CHANNEL);
        let settled = false;
        const finish = (result: boolean) => {
            if (settled) return;
            settled = true;
            channel.close();
            resolve(result);
        };
        channel.onmessage = (event: MessageEvent) => {
            if (isLivenessPong(event.data)) finish(true);
        };
        channel.postMessage(buildLivenessPing(TAB_NONCE));
        setTimeout(() => finish(false), timeoutMs);
    });
}

/**
 * Installs a responder that answers liveness pings from *other* tabs while `isRecordingNow()` returns
 * true. Ignores this tab's own pings via the nonce guard. Returns a cleanup function; a no-op cleanup
 * when BroadcastChannel is unavailable.
 */
export function createRecordingLivenessResponder(isRecordingNow: () => boolean): () => void {
    if (typeof BroadcastChannel === "undefined") return () => {};
    const channel = new BroadcastChannel(RECORDING_LIVENESS_CHANNEL);
    channel.onmessage = (event: MessageEvent) => {
        if (shouldAnswerLivenessPing(event.data, TAB_NONCE) && isRecordingNow()) {
            channel.postMessage(LIVENESS_PONG);
        }
    };
    return () => channel.close();
}
